import { DataTable } from '@ui';
import { useState } from 'react';
import { SortDescriptor } from '@heroui/react';
import { renderInvoiceCell } from './components/renderInvoiceCell';
import { Invoice } from '@/api/apis/case';
import { useOpenInvoice } from '@/hooks/useOpenInvoice';
import { Uuid } from '@/api/types';
import { INVOICE_TABLE_COLUMNS } from './config';
import { sortInvoicesByStatus, toggleSortDirection } from './utils';
import { SortDirection } from './types';
import { InvoiceButtonProps } from './components/InvoiceButton';

export type InvoiceSectionTableProps = {
  invoices: Invoice[];
  caseId: Uuid;
  invoiceButtonVariant?: InvoiceButtonProps['variant'];
  wrapperClassName?: string;
  additionalBaseClassName?: string;
  additionalTdClassName?: string;
  additionalThClassName?: string;
};

export const InvoicesSectionTable = ({
  invoices,
  caseId,
  invoiceButtonVariant = 'sm',
  wrapperClassName = 'rounded-lg',
  additionalBaseClassName = '',
  additionalTdClassName = '',
  additionalThClassName = '',
}: InvoiceSectionTableProps) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const sortedInvoices = sortInvoicesByStatus(invoices, sortDirection);
  const openInvoice = useOpenInvoice(caseId);

  const renderCell = (invoice: Invoice, columnKey: string) =>
    renderInvoiceCell({
      ...invoice,
      onClick: (id: string) => openInvoice(id)(),
      variant: invoiceButtonVariant,
      columnKey,
    });

  const handleSortByChange = () => {
    setSortDirection((prev) => toggleSortDirection(prev));
  };

  const sortBy: SortDescriptor = {
    direction: sortDirection === 'asc' ? 'ascending' : 'descending',
    column: 'status',
  };

  const defaultClasses = {
    baseClassName: 'rounded-xl border-3 border-base-70 shadow-s',
    tdClassName: 'h-[40px] py-0 w-1/2 max-w-[300px]',
    thClassName: 'bg-base-80 first:rounded-bl-none last:rounded-br-none',
  };

  const baseClassName = `${defaultClasses.baseClassName} ${additionalBaseClassName}`.trim();
  const tdClassName = `${defaultClasses.tdClassName} ${additionalTdClassName}`.trim();
  const thClassName = `${defaultClasses.thClassName} ${additionalThClassName}`.trim();

  return (
    <DataTable
      type="infinite"
      wrapperClassName={wrapperClassName}
      baseClassName={baseClassName}
      tdClassName={tdClassName}
      thClassName={thClassName}
      isLoading={false}
      isFetching={false}
      label="Invoice"
      hidePadding
      sortBy={sortBy}
      onSortByChange={handleSortByChange}
      columns={INVOICE_TABLE_COLUMNS}
      renderCell={renderCell}
      data={sortedInvoices}
    />
  );
};
